import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useReactMediaRecorder } from 'react-media-recorder';

const DEFAULT_MIN_SECONDS = 10;
const DEFAULT_MAX_SECONDS = 20;

const RECORDER_ERROR_COPY = {
  permission_denied:
    'Microphone permission was blocked. Enable mic access and try again.',
  media_in_use:
    'Your microphone is already in use. Close other recording apps and try again.',
  no_specified_media_found: 'No microphone was found. Connect a mic and try again.',
  recorder_error: 'Recording failed. Try again.',
  invalid_media_constraints: 'Recording failed. Try again.',
  media_aborted: 'Recording failed. Try again.',
  no_constraints: 'Recording failed. Try again.',
};

const DEFAULT_STATUS_LABELS = {
  idle: 'Ready to record',
  acquiring_media: 'Requesting microphone...',
  delayed_start: 'Requesting microphone...',
  recording: 'Recording',
  stopping: 'Processing preview',
  stopped: 'Ready to preview',
  tooShort: 'Too short',
  tooLong: 'Too long',
  needsConsent: 'Ready for permission',
  ready: 'Ready to submit',
  uploading: 'Uploading',
};

export const formatAudioDropSeconds = (value) => {
  const seconds = Math.max(0, Math.floor(Number(value || 0)));
  return `${seconds}s`;
};

const normalizeDuration = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0) return 0;
  return Math.floor(number);
};

const getRecorderErrorMessage = (status, error) => {
  if (RECORDER_ERROR_COPY[status]) return RECORDER_ERROR_COPY[status];
  if (RECORDER_ERROR_COPY[error]) return RECORDER_ERROR_COPY[error];
  if (error) return RECORDER_ERROR_COPY.recorder_error;
  return '';
};

export default function AudioDropRecorder({
  title = 'Record your voice drop',
  eyebrow = 'Audio drop',
  description = '',
  minSeconds = DEFAULT_MIN_SECONDS,
  maxSeconds = DEFAULT_MAX_SECONDS,
  disabled = false,
  isSubmitting = false,
  startLabel = 'Start recording',
  stopLabel = 'Stop recording',
  rerecordLabel = 'Re-record',
  previewLabel = 'Listen to your take',
  submitLabel = 'Submit voice drop',
  submittingLabel = 'Uploading',
  requireConsent = false,
  hasConsent = true,
  consentSlot = null,
  onSubmit,
  onRecordingReady,
  onReset,
  statusLabels = {},
  className = '',
  ariaLabelPrefix = 'Audio drop',
}) {
  const mergedStatusLabels = useMemo(
    () => ({ ...DEFAULT_STATUS_LABELS, ...statusLabels }),
    [statusLabels],
  );
  const recordingStartedAtRef = useRef(null);
  const elapsedIntervalRef = useRef(null);
  const autoStopTimerRef = useRef(null);
  const stopRequestedRef = useRef(false);
  const stopRecordingRef = useRef(null);
  const clearBlobUrlRef = useRef(null);
  const mediaBlobUrlRef = useRef('');
  const ignoredMediaBlobUrlRef = useRef('');
  const mountedRef = useRef(false);
  const fallbackDurationRef = useRef(0);
  const elapsedSecondsRef = useRef(0);
  const elapsedTickMsRef = useRef(0);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState('');
  const [recordedMimeType, setRecordedMimeType] = useState('');
  const [durationSeconds, setDurationSeconds] = useState(0);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [localError, setLocalError] = useState('');

  const clearRecordingTimers = useCallback(() => {
    if (elapsedIntervalRef.current) {
      window.clearInterval(elapsedIntervalRef.current);
      elapsedIntervalRef.current = null;
    }
    if (autoStopTimerRef.current) {
      window.clearTimeout(autoStopTimerRef.current);
      autoStopTimerRef.current = null;
    }
  }, []);

  const finalizeRecordingDuration = useCallback(() => {
    const startedAt = recordingStartedAtRef.current;
    const hasStartedAt = startedAt !== null && startedAt !== undefined;
    const tickElapsed = elapsedTickMsRef.current / 1000;
    const elapsed = hasStartedAt
      ? Math.max((Date.now() - startedAt) / 1000, tickElapsed)
      : fallbackDurationRef.current ||
        elapsedSecondsRef.current ||
        durationSeconds ||
        elapsedSeconds;
    const finalDuration = normalizeDuration(elapsed);
    clearRecordingTimers();
    recordingStartedAtRef.current = null;
    fallbackDurationRef.current = finalDuration;
    elapsedSecondsRef.current = finalDuration;
    setElapsedSeconds(finalDuration);
    setDurationSeconds((current) => current || finalDuration);
    return finalDuration;
  }, [clearRecordingTimers, durationSeconds, elapsedSeconds]);

  const beginRecordingTimers = useCallback(() => {
    if (
      recordingStartedAtRef.current ||
      elapsedIntervalRef.current ||
      autoStopTimerRef.current
    ) {
      return;
    }
    const startedAt = Date.now();
    recordingStartedAtRef.current = startedAt;
    fallbackDurationRef.current = 0;
    elapsedSecondsRef.current = 0;
    elapsedTickMsRef.current = 0;
    stopRequestedRef.current = false;
    setElapsedSeconds(0);
    setDurationSeconds(0);
    elapsedIntervalRef.current = window.setInterval(() => {
      if (!mountedRef.current) return;
      elapsedTickMsRef.current += 250;
      const nextElapsed = normalizeDuration(
        Math.max(
          (Date.now() - startedAt) / 1000,
          elapsedTickMsRef.current / 1000,
        ),
      );
      elapsedSecondsRef.current = nextElapsed;
      setElapsedSeconds(nextElapsed);
    }, 250);
    autoStopTimerRef.current = window.setTimeout(() => {
      if (!mountedRef.current || stopRequestedRef.current) return;
      stopRequestedRef.current = true;
      finalizeRecordingDuration();
      stopRecordingRef.current?.();
    }, maxSeconds * 1000);
  }, [finalizeRecordingDuration, maxSeconds]);

  const handleRecorderStart = useCallback(() => {
    setLocalError('');
    beginRecordingTimers();
  }, [beginRecordingTimers]);

  const handleRecorderStop = useCallback(
    (blobUrl, blob) => {
      if (!mountedRef.current) return;
      const finalDuration =
        fallbackDurationRef.current || finalizeRecordingDuration();
      const safeBlob = blob || null;
      const safeUrl = blobUrl || '';
      setRecordedBlob(safeBlob);
      setRecordedUrl(safeUrl);
      setRecordedMimeType(safeBlob?.type || '');
      setDurationSeconds(finalDuration);
      setElapsedSeconds(finalDuration);
      onRecordingReady?.({
        blob: safeBlob,
        durationSeconds: finalDuration,
        mimeType: safeBlob?.type || '',
        mediaBlobUrl: safeUrl,
      });
    },
    [finalizeRecordingDuration, onRecordingReady],
  );

  const {
    status,
    error: recorderError,
    startRecording,
    stopRecording,
    mediaBlobUrl,
    clearBlobUrl,
  } = useReactMediaRecorder({
    audio: true,
    onStart: handleRecorderStart,
    onStop: handleRecorderStop,
    blobPropertyBag: { type: 'audio/webm' },
  });

  useEffect(() => {
    stopRecordingRef.current = stopRecording;
  }, [stopRecording]);

  useEffect(() => {
    clearBlobUrlRef.current = clearBlobUrl;
  }, [clearBlobUrl]);

  useEffect(() => {
    mediaBlobUrlRef.current = mediaBlobUrl || '';
  }, [mediaBlobUrl]);

  useEffect(
    () => {
      mountedRef.current = true;
      return () => {
        mountedRef.current = false;
        clearRecordingTimers();
        clearBlobUrlRef.current?.();
      };
    },
    [clearRecordingTimers],
  );

  const hookPreviewUrl =
    mediaBlobUrl && mediaBlobUrl !== ignoredMediaBlobUrlRef.current
      ? mediaBlobUrl
      : '';

  useEffect(() => {
    if (hookPreviewUrl && !recordedUrl) {
      setRecordedUrl(hookPreviewUrl);
    }
  }, [hookPreviewUrl, recordedUrl]);

  useEffect(() => {
    if (status === 'recording') {
      setLocalError('');
      beginRecordingTimers();
      return;
    }
    if (status === 'stopping') {
      finalizeRecordingDuration();
      return;
    }
    const nextError = getRecorderErrorMessage(status, recorderError);
    if (nextError) {
      clearRecordingTimers();
      recordingStartedAtRef.current = null;
      fallbackDurationRef.current = 0;
      elapsedSecondsRef.current = 0;
      elapsedTickMsRef.current = 0;
      stopRequestedRef.current = false;
      setLocalError(nextError);
    }
  }, [
    beginRecordingTimers,
    clearRecordingTimers,
    finalizeRecordingDuration,
    recorderError,
    status,
  ]);

  const resetLocalTake = useCallback(() => {
    clearRecordingTimers();
    recordingStartedAtRef.current = null;
    fallbackDurationRef.current = 0;
    elapsedSecondsRef.current = 0;
    elapsedTickMsRef.current = 0;
    stopRequestedRef.current = false;
    ignoredMediaBlobUrlRef.current = mediaBlobUrlRef.current || '';
    setRecordedBlob(null);
    setRecordedUrl('');
    setRecordedMimeType('');
    setDurationSeconds(0);
    setElapsedSeconds(0);
    setLocalError('');
    clearBlobUrlRef.current?.();
    onReset?.();
  }, [clearRecordingTimers, onReset]);

  const handleStartRecording = () => {
    if (disabled || isSubmitting) return;
    resetLocalTake();
    startRecording();
  };

  const handleStopRecording = () => {
    if (status !== 'recording') return;
    stopRequestedRef.current = true;
    finalizeRecordingDuration();
    stopRecording();
  };

  const handleLoadedMetadata = (event) => {
    const duration = normalizeDuration(event.currentTarget?.duration);
    if (duration) {
      setDurationSeconds(duration);
      fallbackDurationRef.current = duration;
      elapsedSecondsRef.current = duration;
    }
  };

  const resolveSubmitBlob = async () => {
    if (recordedBlob) return recordedBlob;
    const previewUrl = recordedUrl || hookPreviewUrl;
    if (!previewUrl) return null;
    const response = await fetch(previewUrl);
    return response.blob();
  };

  const handleSubmit = async () => {
    if (disabled || isSubmitting) return;
    setLocalError('');
    const previewUrl = recordedUrl || hookPreviewUrl || '';
    const duration = normalizeDuration(durationSeconds);
    if (!previewUrl && !recordedBlob) {
      setLocalError('Record a voice drop before submitting.');
      return;
    }
    if (duration < minSeconds || duration > maxSeconds) {
      setLocalError(`Voice drops must be ${minSeconds}-${maxSeconds} seconds.`);
      return;
    }
    if (requireConsent && !hasConsent) {
      setLocalError('Voice drop consent is required.');
      return;
    }
    let submitBlob = null;
    try {
      submitBlob = await resolveSubmitBlob();
    } catch {
      submitBlob = null;
    }
    if (!submitBlob) {
      setLocalError('Recording failed. Try again.');
      return;
    }
    await onSubmit?.({
      blob: submitBlob,
      durationSeconds: duration,
      mimeType: submitBlob.type || recordedMimeType || 'audio/webm',
      mediaBlobUrl: previewUrl,
    });
  };

  const isAcquiring = status === 'acquiring_media' || status === 'delayed_start';
  const isRecording = status === 'recording';
  const isStopping = status === 'stopping';
  const previewUrl = recordedUrl || hookPreviewUrl || '';
  const hasTake = Boolean(previewUrl || recordedBlob);
  const displaySeconds = isRecording ? elapsedSeconds : durationSeconds;
  const hasValidDuration =
    durationSeconds >= minSeconds && durationSeconds <= maxSeconds;
  const tooShort = hasTake && durationSeconds > 0 && durationSeconds < minSeconds;
  const tooLong = hasTake && durationSeconds > maxSeconds;
  const needsConsent = hasTake && hasValidDuration && requireConsent && !hasConsent;
  const canSubmit =
    hasTake &&
    hasValidDuration &&
    (!requireConsent || hasConsent) &&
    !disabled &&
    !isSubmitting &&
    !isAcquiring &&
    !isRecording &&
    !isStopping;
  const startDisabled = disabled || isSubmitting || isAcquiring || isRecording || isStopping;
  const stateKey = isSubmitting
    ? 'uploading'
    : isAcquiring
      ? 'acquiring_media'
      : isRecording
        ? 'recording'
        : isStopping
          ? 'stopping'
          : tooShort
            ? 'tooShort'
            : tooLong
              ? 'tooLong'
              : needsConsent
                ? 'needsConsent'
                : hasTake && hasValidDuration
                  ? 'ready'
                  : hasTake
                    ? 'stopped'
                    : 'idle';
  const stateLabel = mergedStatusLabels[stateKey] || mergedStatusLabels.idle;
  const progressPercent = Math.min(100, (displaySeconds / maxSeconds) * 100);
  const stateClassName = `is-${stateKey.replace(/_/g, '-')}`;

  return (
    <section
      className={`audio-drop-recorder ${stateClassName} ${className}`.trim()}
      aria-label={`${ariaLabelPrefix} recorder`}
    >
      <div className="audio-drop-recorder-panel">
        <div className="audio-drop-recorder-header">
          <div>
            {eyebrow ? (
              <p className="audio-drop-recorder-eyebrow">{eyebrow}</p>
            ) : null}
            <h4>{title}</h4>
          </div>
          <span className="audio-drop-recorder-status-pill" aria-live="polite">
            {stateLabel}
          </span>
        </div>

        {description ? (
          <p className="audio-drop-recorder-description">{description}</p>
        ) : null}

        <div className="audio-drop-recorder-activity" aria-hidden="true">
          {Array.from({ length: 8 }).map((_, index) => (
            <span key={index} style={{ '--bar-index': index }} />
          ))}
        </div>

        <div className="audio-drop-recorder-time-row">
          <strong>{formatAudioDropSeconds(displaySeconds)}</strong>
          <span>
            {minSeconds}-{maxSeconds}s
          </span>
        </div>
        <div
          className="audio-drop-recorder-progress"
          role="progressbar"
          aria-label={`${ariaLabelPrefix} recording duration`}
          aria-valuemin="0"
          aria-valuemax={maxSeconds}
          aria-valuenow={Math.min(displaySeconds, maxSeconds)}
        >
          <span style={{ width: `${progressPercent}%` }} />
        </div>

        {previewUrl ? (
          <div className="audio-drop-recorder-preview">
            <span>{previewLabel}</span>
            <audio
              controls
              preload="metadata"
              src={previewUrl}
              onLoadedMetadata={handleLoadedMetadata}
            />
          </div>
        ) : null}

        {consentSlot}

        <div className="audio-drop-recorder-actions">
          {isRecording ? (
            <button
              type="button"
              className="hitlab-secondary-btn audio-drop-recorder-stop"
              onClick={handleStopRecording}
              disabled={disabled || isSubmitting}
            >
              {stopLabel}
            </button>
          ) : (
            <button
              type="button"
              className="hitlab-secondary-btn audio-drop-recorder-start"
              onClick={handleStartRecording}
              disabled={startDisabled}
            >
              {hasTake ? rerecordLabel : startLabel}
            </button>
          )}
          {hasTake ? (
            <button
              type="button"
              className="hitlab-primary-btn audio-drop-recorder-submit"
              onClick={handleSubmit}
              disabled={!canSubmit}
            >
              {isSubmitting ? submittingLabel : submitLabel}
            </button>
          ) : null}
        </div>

        {tooShort || tooLong ? (
          <p className="audio-drop-recorder-help" aria-live="polite">
            Voice drops must be {minSeconds}-{maxSeconds} seconds.
          </p>
        ) : null}
        {needsConsent ? (
          <p className="audio-drop-recorder-help" aria-live="polite">
            Check permission to submit this take.
          </p>
        ) : null}
        {localError ? (
          <p className="audio-drop-recorder-error" role="alert">
            {localError}
          </p>
        ) : null}
      </div>
    </section>
  );
}
